import { z } from "zod"
import type { Display } from "@/components/layout/display-options"
import { aircraftColumnSchema, aircraftGroupingSchema } from "./aircraft"
import {
  type Column,
  columnSchema,
  DEFAULT_COLUMNS,
  withNumber,
} from "./columns"
import { type Grouping, groupingSchema } from "./grouping"
import { revisionColumnSchema, revisionGroupingSchema } from "./revisions"
import {
  columnsParam,
  type Direction,
  directionSchema,
  nextSort,
} from "./table"
import { tabSchema } from "./tabs"

export const REVISIONS = { limit: 500 }

export const TASKS = { limit: 10000 }

export const CHECKS = { limit: 1000 }

export const ENROLMENTS = { limit: 1000 }

const DEFAULT_GROUPING: Grouping = "ata_chapter"

export const programmeSearchSchema = z.object({
  tab: tabSchema.optional().catch(undefined),
  revision: z.string().optional().catch(undefined),
  group: groupingSchema.or(z.literal("none")).optional().catch(undefined),
  subgroup: groupingSchema.optional().catch(undefined),
  columns: z.array(columnSchema).optional().catch(undefined),
  sort: columnSchema.optional().catch(undefined),
  direction: directionSchema.optional().catch(undefined),
  ata: z.array(z.string()).optional().catch(undefined),
  phase: z.array(z.string()).optional().catch(undefined),
  search: z.string().optional().catch(undefined),
  wide: z.boolean().optional().catch(undefined),
  aircraft_sort: aircraftColumnSchema.optional().catch(undefined),
  aircraft_direction: directionSchema.optional().catch(undefined),
  aircraft_columns: z.array(aircraftColumnSchema).optional().catch(undefined),
  aircraft_group: aircraftGroupingSchema.optional().catch(undefined),
  aircraft_model: z.array(z.string()).optional().catch(undefined),
  revision_sort: revisionColumnSchema.optional().catch(undefined),
  revision_direction: directionSchema.optional().catch(undefined),
  revision_columns: z.array(revisionColumnSchema).optional().catch(undefined),
  revision_group: revisionGroupingSchema.optional().catch(undefined),
  revision_status: z.array(z.string()).optional().catch(undefined),
})

export type ProgrammeSearch = z.infer<typeof programmeSearchSchema>

export type ItemsDisplay = Display<Grouping> & {
  columns: Column[]
  sort: Column
  direction: Direction
}

export function displayOf(search: ProgrammeSearch): ItemsDisplay {
  const group =
    search.group === "none" ? undefined : (search.group ?? DEFAULT_GROUPING)
  return {
    group,
    subgroup: group ? search.subgroup : undefined,
    columns: search.columns ? withNumber(search.columns) : DEFAULT_COLUMNS,
    sort: search.sort ?? "number",
    direction: search.direction ?? "asc",
  }
}

export function groupingSearch({
  group,
  subgroup,
}: Display<Grouping>): Partial<ProgrammeSearch> {
  return {
    group: group === DEFAULT_GROUPING ? undefined : (group ?? "none"),
    subgroup,
  }
}

export function columnsSearch(columns: Column[]): Partial<ProgrammeSearch> {
  return { columns: columnsParam(columns, DEFAULT_COLUMNS) }
}

export function sortSearch(
  display: ItemsDisplay,
  column: Column,
): Partial<ProgrammeSearch> {
  return nextSort(display, column, "number")
}
